import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { ArrowUp, ArrowDown } from '@dls/assets/icons';
import { InputText, theme } from '@dls/web';
import { forwardRef, useRef, useState, useImperativeHandle, useEffect, Fragment as Fragment$1 } from 'react';
import debounce from '../../helpers/debounce.js';
import { DropdownInputInfiniteScrollContainer, DropdownIconWrapper, DropdownInputWrapper, DropdownInputOptionViewport, DropdownInputOptionItem, Backdrop } from './styles.js';
var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, {
  enumerable: true,
  configurable: true,
  writable: true,
  value
}) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {})) if (__hasOwnProp.call(b, prop)) __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols) for (var prop of __getOwnPropSymbols(b)) {
    if (__propIsEnum.call(b, prop)) __defNormalProp(a, prop, b[prop]);
  }
  return a;
};
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source) if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0) target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols) for (var prop of __getOwnPropSymbols(source)) {
    if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop)) target[prop] = source[prop];
  }
  return target;
};
const findSelectedText = (items, selectedValue) => {
  var _a;
  return ((_a = items.find(i => i.value === selectedValue)) == null ? void 0 : _a.text) || "";
};
const InfiniteScrollDropdownInput = forwardRef((_a, ref) => {
  var _b = _a,
    {
      items,
      label = "",
      onChange,
      defaultValue,
      value,
      bgColor = "haze",
      onLoadMore,
      startPageNumber = 0,
      isLoading,
      onSearch,
      hasNextPage
    } = _b,
    rest = __objRest(_b, ["items", "label", "onChange", "defaultValue", "value", "bgColor", "onLoadMore", "startPageNumber", "isLoading", "onSearch", "hasNextPage"]);
  const containerRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(startPageNumber);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectValue, setSelectValue] = useState(defaultValue || value);
  const [options, setOptions] = useState(items);
  const [hasSearch, setHasSearch] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedText, setSelectedText] = useState(() => findSelectedText(items, selectValue));
  useImperativeHandle(ref, () => inputRef.current);
  const IconDropdown = showDropdown ? ArrowUp : ArrowDown;
  const inputRef = useRef(selectedText);
  useEffect(() => {
    if (initialLoading && !isLoading && items) {
      if (inputRef.current) {
        const text = findSelectedText(items, selectValue);
        inputRef.current.value = text;
        setSelectedText(text);
        setInitialLoading(false);
      }
    }
  }, [initialLoading, isLoading, items, selectValue]);
  useEffect(() => {
    setSelectValue(value);
  }, [value]);
  useEffect(() => {
    if (items) {
      setOptions(items);
    }
  }, [items]);
  const handleFocus = () => {
    setShowDropdown(true);
  };
  const handleLoadMore = event => {
    if (!isLoading) {
      const element = event.target;
      const isLoadMore = element.scrollHeight - element.scrollTop <= element.clientHeight + 1 && hasNextPage;
      if (isLoadMore) {
        onLoadMore == null ? void 0 : onLoadMore({
          search: inputRef == null ? void 0 : inputRef.current.value,
          pageNumber: currentPage + 1
        }).then(res => {
          setCurrentPage(currentPage + 1);
          return res;
        });
      }
    }
  };
  const handleResetOptions = () => {
    if (hasSearch) {
      onLoadMore == null ? void 0 : onLoadMore({
        search: "",
        pageNumber: startPageNumber
      }).then(res => {
        setCurrentPage(startPageNumber);
        setHasSearch(false);
        return res;
      });
    }
  };
  const handleChange = item => {
    setSelectValue(item.value);
    setSelectedText(item.text);
    setShowDropdown(false);
    handleResetOptions();
    if (onChange) {
      onChange(item);
    }
    if (inputRef.current) {
      inputRef.current.value = item.text;
    }
  };
  const handleSearch = () => {
    setHasSearch(true);
    debounce(() => {
      var _a2;
      setCurrentPage(startPageNumber);
      onSearch == null ? void 0 : onSearch((_a2 = inputRef.current) == null ? void 0 : _a2.value);
    }, 300)();
  };
  const handleBackdropClick = () => {
    setShowDropdown(false);
    if (selectedText) {
      if (inputRef.current) {
        inputRef.current.value = selectedText;
        handleResetOptions();
      }
    }
  };
  const handleIconClick = () => {
    var _a2;
    (_a2 = inputRef.current) == null ? void 0 : _a2.focus();
    handleFocus();
  };
  return /* @__PURE__ */jsxs(DropdownInputInfiniteScrollContainer, {
    ref: containerRef,
    onFocus: handleFocus,
    children: [/* @__PURE__ */jsx(InputText, __spreadValues(__spreadValues({
      ref: inputRef,
      label,
      defaultValue: selectedText,
      onChange: handleSearch,
      bgColor
    }, {
      autoComplete: "off"
    }), rest)), /* @__PURE__ */jsx(DropdownIconWrapper, {
      onClick: handleIconClick,
      children: /* @__PURE__ */jsx(IconDropdown, {
        size: 24,
        color: theme.main.colours.secondary.d1
      })
    }), showDropdown && /* @__PURE__ */jsxs(Fragment, {
      children: [/* @__PURE__ */jsx(DropdownInputWrapper, {
        children: /* @__PURE__ */jsx(DropdownInputOptionViewport, {
          onScroll: handleLoadMore,
          children: /* @__PURE__ */jsxs(Fragment$1, {
            children: [options.map((item, idx) => /* @__PURE__ */jsx(DropdownInputOptionItem, {
              className: item.value === selectValue ? "selected" : "",
              onClick: () => handleChange(item),
              children: /* @__PURE__ */jsx("span", {
                children: item.text
              })
            }, idx)), isLoading && /* @__PURE__ */jsx(DropdownInputOptionItem, {
              children: /* @__PURE__ */jsx("span", {
                children: "Loading..."
              })
            })]
          })
        })
      }), /* @__PURE__ */jsx(Backdrop, {
        onClick: handleBackdropClick
      })]
    })]
  });
});
export { InfiniteScrollDropdownInput as default };